import {
  addAsSpeculated,
  addAsValidated,
  emptySpeculatedArray,
  getAtOffset,
  getFirstSpeculated,
  lengthOf,
  moveFirstSpeculatedToValidated,
  numValidated,
  removeAllSpeculated,
  SpeculatedArray,
} from '../../utils/speculated-array';
import { backoffDelayForAttempt } from '../../utils/backoff';
import { ChannelState, TransitionOutId } from '../../models/thread-updates';
import { ConversationThreadId, WidgetId } from '../../models/aliases';
import {
  isActionMessage,
  isStateUpdateMessage,
  isTextMessage,
  Message,
  StateUpdateMessage,
  WidgetActionMessage,
} from '../../models/threads';
import {
  isMatchingMessages,
  reduceActionToMessages,
} from './threads-optimistic-updates';
import { paperFinderStateApi } from './widget-state';

import {
  asyncThunkCreator,
  buildCreateSlice,
  current,
  Draft,
} from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

interface AddMessageParams {
  threadId: ConversationThreadId;
  baseUrl: string;
  message: Message;
}

interface OptimisticUpdateParams {
  threadId: ConversationThreadId;
  baseUrl: string;
  message: WidgetActionMessage;
}

interface BaseUrlAndThreadId {
  threadId: ConversationThreadId;
  baseUrl: string;
}
interface MessagesAndThreadId {
  threadId: ConversationThreadId;
  messages: Array<Message>;
  speculated?: boolean;
}

interface ErrorsAndThreadId {
  threadId: ConversationThreadId;
  previousErrors: Array<string>;
}

interface ChannelStateAndThreadId {
  threadId: ConversationThreadId;
  channel: ChannelState;
}

// Structures for efficient lookups in the conversation messages
// These structures will be updated incrementally as each message arrives

export interface WidgetLookups {
  stateIndexes: SpeculatedArray<number>;
}

export interface ThreadLookups {
  textIndexes: Array<number>; // no such thing as optimistic text messages, so can store in just an array
  lastWidgetId: WidgetId | undefined;
}

// because we can't store objects such as WebSocket (needed for cleaning) in the redux store
// we offload those and wrap them in a cleanup/transition out function, and only store
// an id of the transition in redux

// if we have a state we're transitioning to after, we pass it to the function
// to let it decide what cleanup (if any) it needs to do
type TransitionOutHandler = (toState: ChannelState | undefined) => void;
const transitionOutRegistry: Map<TransitionOutId, TransitionOutHandler> =
  new Map();

function registerTransitionOut(f: TransitionOutHandler): TransitionOutId {
  const id = uuidv4();
  transitionOutRegistry.set(id, f);
  return id;
}

function activateTransitionOut(
  cid: TransitionOutId | undefined,
  toState: ChannelState | undefined,
): void {
  if (cid !== undefined) {
    const transitionOut = transitionOutRegistry.get(cid);
    transitionOut && transitionOut(toState);
    transitionOutRegistry.delete(cid);
  }
}

// ------------------------ //
// Redux state + main logic //
// ------------------------ //

interface ConversationsState {
  channelStates: Record<ConversationThreadId, ChannelState>;
  conversations: Record<ConversationThreadId, SpeculatedArray<Message>>;
  baseUrls: Record<ConversationThreadId, string>;
  threadsLookups: Record<ConversationThreadId, ThreadLookups>;
  widgetsLookups: Record<WidgetId, WidgetLookups>;
}

const initialState: ConversationsState = {
  conversations: {},
  channelStates: {},
  baseUrls: {},
  widgetsLookups: {},
  threadsLookups: {},
};

// creating your own createSlice prevents some sort of dependency loop
const createSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator },
});

export const conversationSlice = createSlice({
  name: 'paperFinder/conversationThreads',
  initialState,
  reducers: (create) => ({
    // basic channels state update
    writeChannelState: create.reducer<ChannelStateAndThreadId>(
      (state, action) => {
        const { threadId, channel } = action.payload;
        const currentChannelState = state.channelStates[threadId];

        // clean current before writing a new one
        currentChannelState &&
          activateTransitionOut(currentChannelState.transitionOutId, channel);

        state.channelStates[threadId] = channel;
      },
    ),
    writeBaseUrl: create.reducer<BaseUrlAndThreadId>((state, action) => {
      const { threadId, baseUrl } = action.payload;
      state.baseUrls[threadId] = baseUrl;
    }),
    // clear the channel state for a specific threadId
    clearState: create.reducer<ConversationThreadId>((state, action) => {
      const threadId = action.payload;
      const currentChannelState = state.channelStates[threadId];

      // clean current before writing a new one
      currentChannelState &&
        activateTransitionOut(currentChannelState.transitionOutId, undefined);

      delete state.channelStates[threadId];
    }),
    // clear the channel state for a specific threadId, only parts that weren't validated by the server
    clearSpeculatedConversation: create.reducer<ConversationThreadId>(
      (state, action) => {
        const threadId = action.payload;
        const conversationState = state.conversations[threadId];

        if (conversationState) {
          // clear the speculated widget lookups
          for (const message of current(conversationState.speculated)) {
            if (isStateUpdateMessage(message)) {
              removeAllSpeculated(
                state.widgetsLookups[message.widgetId].stateIndexes,
              );
            }
          }
          // clean current before writing a new one
          removeAllSpeculated(conversationState);
        }
      },
    ),
    // load messages into the conversation state and update per widget lookups
    receiveMessages: create.reducer<MessagesAndThreadId>((state, action) => {
      const { threadId, messages, speculated } = action.payload;

      for (const message of messages) {
        handleReceiveMessage(state, threadId, message, speculated ?? false);
      }
    }),
    // wait a P amount of time based on the backoffStrategy and how many errors where there before
    transitionToBackoff: create.asyncThunk(
      async (params: ErrorsAndThreadId, { dispatch }) => {
        const { threadId, previousErrors } = params;
        const delay = backoffDelayForAttempt(previousErrors.length);

        if (delay === null) {
          // all backoffs exhausted, moving to error state
          dispatch(
            conversationSlice.actions.writeChannelState({
              threadId,
              channel: {
                type: 'error',
                errors: previousErrors,
                transitionOutId: undefined,
              },
            }),
          );
        } else {
          // waiting for an amount of time appropriate to backoff stop
          const timeoutId: number = setTimeout(() => {
            dispatch(
              conversationSlice.actions.transitionToOpening({
                threadId,
                previousErrors,
              }),
            );
          }, delay) as unknown as number;

          dispatch(
            conversationSlice.actions.writeChannelState({
              threadId,
              channel: {
                type: 'backoff-wait',
                previousErrors,
                transitionOutId: registerTransitionOut(() =>
                  clearTimeout(timeoutId),
                ),
              },
            }),
          );
        }
      },
    ),
    // try to establish communication (first bulk get of messages and then establish a web socket)
    transitionToOpening: create.asyncThunk(
      async (params: ErrorsAndThreadId, { dispatch, getState }) => {
        const { threadId, previousErrors } = params;

        try {
          dispatch(
            conversationSlice.actions.writeChannelState({
              threadId,
              channel: {
                type: 'opening',
                previousErrors,
                transitionOutId: undefined,
              },
            }),
          );

          let state = conversationSlice.selectSlice(getState() as any);

          // We want to get messages from the offset that is already in sync
          // with the server, we can't use lengthOf(state.conversations[threadId])
          // because it will cause us to skip server validated messages, just
          // because we have some local messages that never got validated
          let fromOffset = numValidated(state.conversations[threadId]);

          const baseUrl = state.baseUrls[threadId] ?? '';

          // fetch initial bulk of messages
          const bulkUrl = `${baseUrl}threads/${encodeURI(
            threadId,
          )}/messages?from_offset=${fromOffset}`;
          const response = await fetch(bulkUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          if (!response.ok) {
            dispatch(
              conversationSlice.actions.transitionToBackoff({
                threadId,
                previousErrors: [
                  `Bulk api request failed with status: ${response.status}`,
                  ...previousErrors,
                ],
              }),
            );
            return;
          }

          const messages: Array<Message> = await response.json();
          // before processing the messages, clear all speculated messages as we've reconnected
          // and the local state might not match the updates we will get from the server
          dispatch(
            conversationSlice.actions.clearSpeculatedConversation(threadId),
          );

          dispatch(
            conversationSlice.actions.receiveMessages({ threadId, messages }),
          );

          state = conversationSlice.selectSlice(getState() as any);
          fromOffset = lengthOf(state.conversations[threadId]);

          // connect websocket for updates
          const ws = new WebSocket(
            `${baseUrl}threads/${encodeURI(
              threadId,
            )}/messages/ws?from_offset=${fromOffset}`,
          );

          const handleOpen = () => {
            const state = conversationSlice.selectSlice(getState() as any);
            const currentChannelState = state.channelStates[threadId];

            // transition to OK state
            if (currentChannelState?.type === 'opening') {
              dispatch(
                conversationSlice.actions.writeChannelState({
                  threadId,
                  channel: {
                    type: 'ok',
                    transitionOutId: registerTransitionOut(cleanUp),
                  },
                }),
              );
            } else {
              console.warn({ msg: 'socket invalid state' });
            }

            // after the socket opened, any new errors start a new error chain, so we
            // overwrite the error listener to reset the chain
            ws.removeEventListener('error', errorBeforeOpen);
            ws.addEventListener('error', errorAfterOpen);
            ws.addEventListener('close', closeAfterOpen);
          };

          // errors after open start a new chain of backoff, errors before add
          // to the chain and accumulate
          const errorBeforeOpen = () => {
            dispatch(
              conversationSlice.actions.transitionToBackoff({
                threadId,
                previousErrors: [`Socket connection failed`, ...previousErrors],
              }),
            );
          };

          const errorAfterOpen = () => {
            dispatch(
              conversationSlice.actions.transitionToBackoff({
                threadId,
                previousErrors: [`Socket connection failed`],
              }),
            );
          };

          const handleMessage = (event: MessageEvent): void => {
            const message = JSON.parse(event.data);
            dispatch(
              conversationSlice.actions.receiveMessages({
                threadId,
                messages: [message],
              }),
            );
          };

          // we don't care about close before because error will also be called
          const closeAfterOpen = () => {
            dispatch(
              conversationSlice.actions.transitionToBackoff({
                threadId,
                previousErrors: [`Socket closed by server`],
              }),
            );
          };

          const cleanUp = (toState: ChannelState | undefined) => {
            // no need to close the websocket on the transition from "opening" to "ok"
            if (toState?.type !== 'ok') {
              ws.removeEventListener('error', errorBeforeOpen);
              ws.removeEventListener('error', errorAfterOpen);
              ws.removeEventListener('close', closeAfterOpen);
              ws.removeEventListener('message', handleMessage);
              ws.removeEventListener('open', handleOpen);
              ws.close();
            }
          };

          dispatch(
            conversationSlice.actions.writeChannelState({
              threadId,
              channel: {
                type: 'opening',
                previousErrors,
                transitionOutId: registerTransitionOut(cleanUp),
              },
            }),
          );

          // connect all socket listeners
          ws.addEventListener('open', handleOpen);
          ws.addEventListener('message', handleMessage);
          ws.addEventListener('error', errorBeforeOpen);
        } catch (error: any) {
          dispatch(
            conversationSlice.actions.transitionToBackoff({
              threadId,
              previousErrors: [error.toString(), ...previousErrors],
            }),
          );
        }
      },
    ),

    // public actions to start/stop listening on a particular thread
    registerForUpdates: create.asyncThunk(
      async (params: BaseUrlAndThreadId, { dispatch }) => {
        const { threadId, baseUrl } = params;
        dispatch(
          conversationSlice.actions.writeBaseUrl({
            threadId,
            baseUrl,
          }),
        );
        await dispatch(
          conversationSlice.actions.transitionToOpening({
            threadId,
            previousErrors: [],
          }),
        );
      },
    ),
    unregisterFromUpdates: create.asyncThunk(
      async (threadId: ConversationThreadId, { dispatch }) => {
        dispatch(conversationSlice.actions.clearState(threadId));
      },
    ),
    // perform a local (optimistic) state update based on an action message
    optimisticUpdateStateFromActionMessage: create.asyncThunk(
      async (params: OptimisticUpdateParams, { getState, dispatch }) => {
        const { threadId, baseUrl, message } = params;

        const fullState = getState() as any;
        const conversationsStateSlice =
          conversationSlice.selectSlice(fullState);

        const threadMessages = conversationsStateSlice.conversations[threadId];
        const actedOnStateMessage = getAtOffset(
          threadMessages,
          message.predecessorOffsetInThread,
        ) as StateUpdateMessage;

        const widgetStateResult =
          paperFinderStateApi.endpoints.widgetState.select({
            baseUrl,
            stateId: actedOnStateMessage.stateId,
          })(getState() as any);
        const widgetState = widgetStateResult.data;

        if (widgetState) {
          const [optimisticStateMessage, updatedWidgetState] =
            reduceActionToMessages(threadMessages, widgetState, message);

          if (optimisticStateMessage) {
            // optimistically populate the conversation with the action and its corresponding state update message
            dispatch(
              conversationSlice.actions.receiveMessages({
                threadId,
                messages: [message, optimisticStateMessage],
                speculated: true,
              }),
            );

            // optimistically populate the state cache with the optimistically computed state
            // but only if it doesn't exist yet
            const cacheKey = {
              baseUrl,
              stateId: optimisticStateMessage.stateId,
            };
            const currentCacheEntry =
              paperFinderStateApi.endpoints.widgetState.select(cacheKey)(
                fullState,
              );
            if (!currentCacheEntry.isSuccess) {
              const actionThunk = paperFinderStateApi.util.upsertQueryData(
                'widgetState',
                cacheKey,
                updatedWidgetState,
              );

              dispatch(actionThunk as any);
            }
          }
        }
      },
    ),
    // add a (typically an action) message
    addMessageToThread: create.asyncThunk(
      async (params: AddMessageParams, { dispatch, rejectWithValue }) => {
        const { threadId, baseUrl, message } = params;
        // perform an optimistic update if applicable
        if (message.kind === 'action') {
          dispatch(
            conversationSlice.actions.optimisticUpdateStateFromActionMessage({
              threadId,
              baseUrl,
              message,
            }),
          );
        }

        const updateUrl = `${baseUrl}threads/${encodeURI(threadId)}/messages`;
        const response = await fetch(updateUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(message),
        });

        if (!response.ok) {
          rejectWithValue(`Request to send message failed: ${response}`);
        }
      },
    ),
  }),
  selectors: {
    getBaseUrl: (
      state: ConversationsState,
      threadId: ConversationThreadId,
    ): string => {
      return state.baseUrls[threadId] ?? '';
    },
  },
});

function handleReceiveMessage(
  state: Draft<ConversationsState>,
  threadId: ConversationThreadId,
  message: Message,
  speculated: boolean,
) {
  // if it's the first message create the list of messages for this thread
  if (state.conversations[threadId] === undefined) {
    state.conversations[threadId] = emptySpeculatedArray();
  }

  // if this is the first state update for this widgetId, create initial cache structure
  if (
    isStateUpdateMessage(message) &&
    state.widgetsLookups[message.widgetId] === undefined
  ) {
    state.widgetsLookups[message.widgetId] = {
      stateIndexes: emptySpeculatedArray(),
    };
  }

  const threadCache = state.threadsLookups[threadId];
  if (isTextMessage(message)) {
    if (!threadCache) {
      // create initial thread cache
      state.threadsLookups[threadId] = {
        textIndexes: [message.offsetInThread],
        lastWidgetId: undefined,
      };
    } else {
      // update existing thread cache
      threadCache.textIndexes.push(message.offsetInThread);
    }
  }
  if (isActionMessage(message) && message.action.kind === 'new-widget') {
    threadCache.lastWidgetId = message.action.widgetId;
  }

  const existingMessages = state.conversations[threadId];

  // in case we get new speculated messages, and the offsets match correctly, just add as speculated
  if (speculated) {
    if (message.offsetInThread === lengthOf(existingMessages)) {
      addAsSpeculated(existingMessages, [message]);
      if (isStateUpdateMessage(message)) {
        addAsSpeculated(state.widgetsLookups[message.widgetId].stateIndexes, [
          message.offsetInThread,
        ]);
      }
    } else {
      console.warn('speculated message out of order, should never happen');
    }
  } else {
    const firstExistingSpeculatedMessage = getFirstSpeculated(existingMessages);
    if (firstExistingSpeculatedMessage !== undefined) {
      if (isMatchingMessages(firstExistingSpeculatedMessage, message)) {
        // local message matches server version, it's no longer speculated, so move to validated
        moveFirstSpeculatedToValidated(existingMessages);
        if (isStateUpdateMessage(message)) {
          moveFirstSpeculatedToValidated(
            state.widgetsLookups[message.widgetId].stateIndexes,
          );
        }
      } else {
        // mismatch between local and server version, remove all speculated messages
        for (const message of existingMessages.speculated) {
          if (isStateUpdateMessage(message)) {
            removeAllSpeculated(
              state.widgetsLookups[message.widgetId].stateIndexes,
            );
          }
        }
        removeAllSpeculated(existingMessages);

        // add the new incoming message as a validated one
        addAsValidated(existingMessages, [message]);
        if (isStateUpdateMessage(message)) {
          addAsValidated(state.widgetsLookups[message.widgetId].stateIndexes, [
            message.offsetInThread,
          ]);
        }
      }
    } else {
      // no speculated messages in local state, just add a validated message
      if (message.offsetInThread === lengthOf(existingMessages)) {
        addAsValidated(existingMessages, [message]);
        if (isStateUpdateMessage(message)) {
          addAsValidated(state.widgetsLookups[message.widgetId].stateIndexes, [
            message.offsetInThread,
          ]);
        }
      } else {
        console.warn('server message out of order, should never happen');
      }
    }
  }
}

// public actions
export const { registerForUpdates, unregisterFromUpdates, addMessageToThread } =
  conversationSlice.actions;

export const { getBaseUrl } = conversationSlice.selectors;
